import React, { useEffect, useState } from 'react';
import { Flex, Stack } from '@mantine/core';
import { Actions, DockLocation, Layout, Model } from 'flexlayout-react';
import type { TabNode } from 'flexlayout-react';
import DataViewerSidebar from '@/layouts/DataViewer/DataViewerSidebar.tsx';
import { useViewDataset } from '@/hooks/useViewDataset.ts';
import { baseJson, chatTabId, dataTabId, mainTabId, stepEditTabId } from './layout';
import { useViewChatSidebar } from '@/layouts/AutomationPageWrapper/hooks/useViewChatSidebar.ts';
import ResultsChatSidebar from '@/layouts/ResultsChat/ResultsChatSidebar.tsx';
import EditorHeader from '@/layouts/AutomationPageWrapper/Header/Header.tsx';
import { BOX_SHADOW } from '@/utils/constants.ts';
import { useMatch } from 'react-router-dom';
import StepSidebar from '@/pages/Editor/components/StepSidebar/StepSidebar.tsx';
import { Automation } from '@/types/automations.ts';

type props = {
  automation: Automation;
  isOuterWrapper?: boolean;
  children?: React.ReactNode;
};

const defaultWeights = [60, 20, 20];

const addTab = (model: Model, tabId: string, insertIndex: number, weights?: number[]) => {
  const tabsetId = model.getNodeById('row')?.getChildren().at(insertIndex)?.getId();
  if (!tabsetId) return;

  model.doAction(
    Actions.addNode({ type: 'tab', id: tabId, component: tabId }, tabsetId, DockLocation.RIGHT, -1),
  );
  model.doAction(Actions.adjustWeights('row', weights || defaultWeights));
};

const deleteTab = (model: Model, tabId: string, weights?: number[]) => {
  model.doAction(Actions.deleteTab(tabId));
  model.doAction(Actions.adjustWeights('row', weights || defaultWeights));
};

// this wraps all the automation pages but the relevant side panels vary based on the page
export default function AutomationPageWrapper({
  automation,
  isOuterWrapper = true,
  children,
}: props) {
  const [model] = useState(() => Model.fromJson(baseJson as any));

  const { datasetId } = useViewDataset();

  const { showChat } = useViewChatSidebar();

  // const isResultsPage = !!useMatch('/automations/:automationId/results/:runId');
  const isRunsPage = !!useMatch('/automations/:automationId/runs/*');
  const isEditorPage = !!useMatch('/automations/:automationId/editor');

  useEffect(() => {
    if (!isRunsPage) return;

    const exists = model.getNodeById(dataTabId);
    if (datasetId && !exists) {
      addTab(model, dataTabId, 0);
    }
    if (!datasetId && exists) {
      deleteTab(model, dataTabId);
    }
  }, [datasetId, isRunsPage, model]);

  useEffect(() => {
    if (!isEditorPage) return;

    const exists = model.getNodeById(stepEditTabId);
    if (!exists) {
      addTab(model, stepEditTabId, 0, [66, 33]);
    }
  }, [model, isEditorPage]);

  useEffect(() => {
    const exists = model.getNodeById(chatTabId);

    if (showChat && !exists) {
      addTab(model, chatTabId, -1);
    }
    if (!showChat && exists) {
      model.doAction(Actions.deleteTab(chatTabId));
    }
  }, [showChat, model]);

  const factory = (node: TabNode) => {
    const component = node.getId();
    switch (component) {
      case mainTabId:
        return (
          <Flex
            w="100%"
            h="100%"
            style={{ boxShadow: datasetId || chatTabId ? BOX_SHADOW : 'none' }}
          >
            {children}
          </Flex>
        );
      case dataTabId:
        return (
          <Flex w="100%" h="100%" style={{ boxShadow: chatTabId ? BOX_SHADOW : 'none' }}>
            <DataViewerSidebar />
          </Flex>
        );
      case chatTabId:
        return (
          <Flex w="100%" h="100%">
            <ResultsChatSidebar />
          </Flex>
        );
      case stepEditTabId:
        return (
          <Flex w="100%" h="100%">
            <StepSidebar />
          </Flex>
        );
      default:
        return <div />;
    }
  };

  if (isOuterWrapper) {
    return (
      <Stack
        w={'100vw'}
        h={'100vh'}
        miw={'100vw'}
        mih="100vh"
        style={{ overflow: 'hidden' }}
        gap={0}
      >
        <EditorHeader automation={automation} />
        <Flex w="100%" h="100%" pos="relative">
          <Layout model={model} factory={factory} />
        </Flex>
      </Stack>
    );
  }

  return (
    <Stack
      w={'100%'}
      h={'100%'}
      miw={'100%'}
      mih="100%"
      style={{ overflow: 'hidden' }}
      gap={0}
      pos={'relative'}
    >
      <EditorHeader automation={automation} />
      <Flex w="100%" h="100%" pos="relative">
        <Layout model={model} factory={factory} />
      </Flex>
    </Stack>
  );
}
